import React, { useEffect, useMemo, useState } from "react";
import { withTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import PropTypes from "prop-types";
import { omit } from "lodash";
import * as Yup from "yup";
import { useFormik } from "formik";

import Stepper from "common/Stepper";
import BreadCrumb from "Components/Common/BreadCrumb";
import AuthorizedPersonDetails from "Components/AuthorizedPersonDetails/AuthorizedPersonDetails";
import DocumentToSubmit from "Components/DocumentsToSubmit";
import Declaration from "Components/Declaration";
import { applicationTypes } from "common/constant";
import Backdrop from "Components/Common/Backdrop";
import { otherUtilityApplicationOptions } from "common/enums";
import NameTransferApplicantDetails from "./NameTransferApplicantDetails";
import { myOtherRequestRoute } from "Routes/constants";

import { getIsDocumentRequired } from "utils/getIsDocumentRequired";
import {
  generateFormData,
  filterEmptyStrings,
  convertDocumentKeys,
} from "utils";

import { useLazyGetOtherConsumerByConsumerNumber } from "apis/User";
import { getNameChangeTransferSchema } from "utils/otherUtilitiesValidationSchema";
import { useApplicantTypeQuery } from "apis/ApplicantType";
import {
  useLazyGetOtherApplicationDetailQuery,
  usePostOtherUtilityApplicationDetailsMutation,
} from "apis/otherApplicationDetails";

const OtherApplicationNameTransfer = ({
  editApplicationQuery,
  handleClose = () => {},
  t,
}) => {
  const [applicationData, setApplicationData] = useState({});
  const [currentStep, setCurrentStep] = useState(0);
  const user = useSelector((state) => state.Login.user);
  const [consumerData, setConsumerData] = useState(null);
  const navigate = useNavigate();

  const [submitApplication, { isLoading }] =
    usePostOtherUtilityApplicationDetailsMutation();
  const { data: { data: { items: applicantTypes } = {} } = {} } =
    useApplicantTypeQuery();

  const [
    getUtilityConsumerDetails,
    {
      isLoading: isConsumerDetailsLoading,
      isFetching: isConsumerDetailsFetching,
    },
  ] = useLazyGetOtherConsumerByConsumerNumber();

  const [
    getApplicationDetails,
    {
      isLoading: isApplicationDataLoading,
      isFetching: isApplicationDataFetching,
    },
  ] = useLazyGetOtherApplicationDetailQuery();

  const initialValues = useMemo(
    () => ({
      id: editApplicationQuery?.applicationId ?? 0,
      utilities:
        otherUtilityApplicationOptions.filter(
          ({ value }) =>
            value ===
            (applicationData?.utilities?.[0] ?? consumerData?.utility?.id)
        ) ?? [],
      applicationTypeId: applicationTypes["Name Change/Transfer"],
      groupId: applicationData?.groupId ?? "",
      utilityConsumerId:
        applicationData?.utilityConsumerId ?? consumerData?.id ?? "",
      consumerName: consumerData?.consumerName || "",
      consumerNumber:
        applicationData?.consumerNumber ?? consumerData?.consumerNumber ?? "",
      applicant: {
        id: applicationData?.applicant?.id ?? 0,
        applicantTypeId: applicationData?.applicant?.applicantType?.id ?? "",
        applicantName: applicationData?.applicant?.applicantName ?? "",
        panNumber: applicationData?.applicant?.panNumber ?? "",
        gstNumber: applicationData?.applicant?.gstNumber ?? "",
        occupancyTypeId: applicationData?.applicant?.occupancyType?.id ?? "",
        isGSTRegistered: applicationData?.applicant?.isGSTRegistered ?? "",
        addressType: applicationData?.applicant?.addressType ?? "",
        correspondenceAddress:
          applicationData?.applicant?.correspondenceAddress ?? "",
        city: applicationData?.applicant?.city ?? "",
        pinCode: applicationData?.applicant?.pinCode ?? "",
        email: applicationData?.applicant?.email ?? "",
        mobileNumber: applicationData?.applicant?.mobileNumber ?? "",
        landLine: applicationData?.applicant?.landLine ?? "",
        alternateEmail: applicationData?.applicant?.alternateEmail
          ? applicationData?.applicant?.alternateEmail?.split(", ")
          : [],
        alternateMobileNumber:
          applicationData?.applicant?.alternateMobileNumber ?? "",
        eBillConfirmation: true,
      },
      authorizedPerson: {
        isAuthorizedPerson: applicationData?.authorizedPerson
          ? applicationData.authorizedPerson.email ===
              applicationData.user.email &&
            applicationData.authorizedPerson.mobileNumber ===
              applicationData.user.mobileNumber
          : true,
        id: applicationData?.authorizedPerson?.id ?? 0,
        salutation: applicationData?.authorizedPerson?.salutation ?? "",
        firstName: applicationData?.authorizedPerson?.firstName ?? "",
        middleName: applicationData?.authorizedPerson?.middleName ?? "",
        lastName: applicationData?.authorizedPerson?.lastName ?? "",
        designation: applicationData?.authorizedPerson?.designation ?? "",
        email: applicationData?.authorizedPerson?.email ?? "",
        mobileNumber: applicationData?.authorizedPerson?.mobileNumber ?? "",
        landLine: applicationData?.authorizedPerson?.landLine ?? "",
        image: "",
        imageUrl: applicationData?.authorizedPerson?.imageUrl ?? "",
        isUnRegisteredPerson: applicationData?.authorizedPerson
          ? applicationData.authorizedPerson.email !== user.email &&
            applicationData.authorizedPerson.mobileNumber !== user.mobileNumber
          : false,
      },
      utilityAddress: {
        premisesAreaId: consumerData?.utilityAddress?.premiseArea?.id ?? "",
      },
      declaration: {
        declarationId: applicationData?.declaration?.id ?? 0,
        isConsumerDuesOutstanding: applicationData?.declaration
          ? !!applicationData.declaration.consumerDuesOutstanding
          : "",
        consumerDuesOutstanding:
          applicationData?.declaration?.consumerDuesOutstanding ?? "",
        isPremisesDuesOutstanding: applicationData?.declaration
          ? !!applicationData.declaration.premisesDuesOutstanding
          : "",
        premisesDuesOutstanding:
          applicationData?.declaration?.premisesDuesOutstanding ?? "",
      },
      extraValues: {
        userMobileNumber: applicationData?.user?.mobileNumber ?? "",
        userEmail: applicationData?.user?.email ?? "",
        requiredDocumentsCount: 0,
        proofOfOwnershipTypes: [],
        applicantType: applicantTypes ?? [],
      },
      inProgressApplicationId: consumerData?.inProgressApplicationId ?? 0,
      isAllApplicationsProcessCompleted:
        consumerData?.isAllApplicationsProcessCompleted ?? false,
      payableBillAmount: consumerData?.payableBillAmount ?? 0,
      parentId:
        applicationData?.documents?.find((document) => document.parentId)
          ?.parentId ?? "",
      documents: applicationData?.documents?.filter(({ id }) => id) ?? [],
      isDraft: true,
      consumerData: consumerData || null,
      proofOfOwnershipDescription: "",
      isIGSTExempted: consumerData?.isIGSTExempted ?? false,
    }),
    [applicationData, consumerData]
  );

  const formik = useFormik({
    enableReinitialize: true,
    initialValues,
    validationSchema: !(
      (consumerData?.inProgressApplicationId !== applicationData?.id &&
        !consumerData?.isAllApplicationsProcessCompleted) ||
      consumerData?.payableBillAmount > 0
    )
      ? getNameChangeTransferSchema[currentStep]
      : Yup.object({
          consumerNumber: Yup.number().required("Consumer number is required"),
        }),

    onSubmit: async (values, actions) => {
      const {
        inProgressApplicationId,
        isAllApplicationsProcessCompleted,
        payableBillAmount,
      } = values;
      try {
        if (currentStep === steps.length - 1) {
          createNameTransferConnection(values);
        } else if (
          inProgressApplicationId &&
          inProgressApplicationId !== applicationData?.id &&
          !isAllApplicationsProcessCompleted
        ) {
          toast.error(
            "There are some incomplete applications associated with this consumer number. You are unable to fill the form."
          );
        } else if (payableBillAmount > 0) {
          toast.error(
            `You have an unpaid amount of ${payableBillAmount} RS. Please pay first to continue.`
          );
        } else {
          setCurrentStep((prev) => prev + 1);
          actions.setTouched({});
        }
      } catch (error) {
        console.error(error);
      }
    },
  });
  const { values, initialValues: formikInitialValues } = formik;

  const {
    documents,
    extraValues: { requiredDocumentsCount },
  } = values;

  const getCustomerDetails = async (utilityId, consumerNumber) => {
    try {
      const { data } = await getUtilityConsumerDetails({
        utilityId,
        consumerNumber,
      });

      setConsumerData(data);

      if (
        editApplicationQuery &&
        formikInitialValues.utilityConsumerId &&
        data.id !== formikInitialValues.utilityConsumerId
      ) {
        setApplicationData({});
      }
    } catch (error) {
      setConsumerData({
        utility: { id: utilityId },
        consumerNumber,
      });
      setApplicationData({});
    }
  };

  const steps = useMemo(
    () => [
      {
        id: 1,
        name: "Applicant Details",
        isCompleted: currentStep >= 1,
        component: (
          <NameTransferApplicantDetails
            formik={formik}
            isOtherUtilities
            getCustomerDetails={getCustomerDetails}
          />
        ),
      },
      {
        id: 2,
        name: "Authorized Person Details",
        isCompleted: currentStep >= 2,
        component: <AuthorizedPersonDetails formik={formik} />,
      },
      {
        id: 3,
        name: "Document to Submit",
        isCompleted: currentStep >= 3,
        component: <DocumentToSubmit formik={formik} />,
      },
      {
        id: 4,
        name: "Declaration",
        isCompleted: currentStep === 4,
        component: <Declaration formik={formik} />,
      },
    ],
    [currentStep, formik]
  );

  useEffect(() => {
    if (editApplicationQuery) {
      try {
        (async () => {
          const { data } = await getApplicationDetails(editApplicationQuery);
          setApplicationData(data);
          await getCustomerDetails(data.utilities[0], data.consumerNumber);
        })();
      } catch (error) {
        console.error(error);
      }
    }
  }, [editApplicationQuery]);

  const validateDocument = () => {
    const requiredUploadedDocumentsCount = documents.filter(
      ({ documentRequiredEnum }) => getIsDocumentRequired(documentRequiredEnum)
    ).length;

    if (
      currentStep === 2 &&
      requiredUploadedDocumentsCount !== requiredDocumentsCount
    ) {
      toast.error("Please upload all required documents");
    }
  };

  const createNameTransferConnection = async (values, isDraft = false) => {
    const imageKeyToRemove = values.authorizedPerson.image
      ? "authorizedPerson.imageUrl"
      : "authorizedPerson.image";
    const cityId = values.applicant.city?.id ?? "";

    // converts document array to key-value pair
    const payloadWithUpdatedDocumentKeys = convertDocumentKeys(values);

    const applicationDetails = omit(
      {
        ...payloadWithUpdatedDocumentKeys,
        applicant: {
          ...payloadWithUpdatedDocumentKeys.applicant,
          alternateEmail: payloadWithUpdatedDocumentKeys.applicant
            .alternateEmail.length
            ? payloadWithUpdatedDocumentKeys.applicant.alternateEmail.join(", ")
            : "",
          cityId,
        },
        isDraft,
      },
      [
        "applicant.city",
        "inProgressApplicationId",
        "isAllApplicationsProcessCompleted",
        "payableBillAmount",
        // "isDraft",
        "consumerNumber",
        "customerDetail",
        "authorizedPerson.isAuthorizedPerson",
        "consumerData",
        "utilityAddress",
        "authorizedPerson.isUnRegisteredPerson",
        "authorizedPerson.isEditablePerson",
        "declaration.isConsumerDuesOutstanding",
        "declaration.isPremisesDuesOutstanding",
        "declaration.isFirmDuesOutstanding",
        "parentId",
        "proofOfOwnershipDescription",
        "documents",
        "consumerName",
        "extraValues",
        "isIGSTExempted",
        imageKeyToRemove,
      ]
    );

    const filteredValues = filterEmptyStrings(applicationDetails);
    // remove entries that contain only id field

    const payload = Object.entries(filteredValues).reduce(
      (res, [key, value]) => {
        if (
          typeof value !== "object" ||
          value instanceof File ||
          Object.keys(value).length > 1
        ) {
          return { ...res, [key]: value };
        }
        return res;
      },
      {}
    );
    const formData = new FormData();
    generateFormData(formData, {
      ...payload,
      declaration: filteredValues.declaration,
      utilities: values.utilities.map(({ value }) => value),
    });

    try {
      await submitApplication(formData);
      // If submitted then navigate to my-request
      if (!editApplicationQuery) {
        navigate(myOtherRequestRoute);
      }
      handleClose();
    } catch (error) {}
  };

  const handleDraft = () => {
    const { utilities, consumerNumber } = formik.values;

    if (!utilities || utilities.length === 0) {
      formik.setFieldError("utilities", "utilities is required");
      formik.setFieldTouched("utilities", true, false);
    }
    if (!consumerNumber) {
      formik.setFieldError("consumerNumber", "Consumer number is required");
      formik.setFieldTouched("consumerNumber", true, false);
      return;
    }

    createNameTransferConnection(formik.values, true);
  };

  return (
    <>
      <div className={!editApplicationQuery ? "h-100" : ""}>
        <Backdrop
          open={
            isApplicationDataLoading ||
            isConsumerDetailsLoading ||
            isApplicationDataFetching ||
            isConsumerDetailsFetching ||
            (!!editApplicationQuery && !consumerData)
          }
        />
        {!editApplicationQuery && (
          <BreadCrumb
            title={t("Name Change/Transfer Connection")}
            pageTitle={t("Other Applications")}
          />
        )}
        <div className="stepper-container">
          <Stepper
            steps={steps}
            handleSubmit={formik.handleSubmit}
            handleDraft={handleDraft}
            validateDocument={validateDocument}
            activeStepIndex={currentStep}
            setActiveStepIndex={setCurrentStep}
            isLoading={isLoading}
            formik={formik}
            isDraftDisabled={
              !!editApplicationQuery &&
              applicationData?.applicationStatus?.name?.toLowerCase() !==
                "saved"
            }
          />
        </div>
      </div>
    </>
  );
};

OtherApplicationNameTransfer.propTypes = {
  editApplicationQuery: PropTypes.object,
  handleClose: PropTypes.func,
  t: PropTypes.func,
};

export default withTranslation()(OtherApplicationNameTransfer);
